import type { Block } from './blocks';
import { ANCHOR_POSITIONS, flipAnchorHorizontally, flipScrimDirectionHorizontally } from './blocks';
import { flipBgFocalX } from './bg-helpers';
import type { BlockSlide, SlideBg } from './types';

export function patchSlideBlock(
  slide: BlockSlide,
  blockId: string,
  patch: Partial<Block>,
): BlockSlide {
  return {
    ...slide,
    blocks: slide.blocks.map((b) => (b.id === blockId ? ({ ...b, ...patch } as Block) : b)),
  };
}

export function removeSlideBlock(slide: BlockSlide, blockId: string): BlockSlide {
  return { ...slide, blocks: slide.blocks.filter((b) => b.id !== blockId) };
}

export function duplicateSlideBlock(slide: BlockSlide, blockId: string, newId: string): BlockSlide {
  const idx = slide.blocks.findIndex((b) => b.id === blockId);
  if (idx < 0) return slide;
  const b = slide.blocks[idx];
  if (!b) return slide;
  const copy = JSON.parse(JSON.stringify(b)) as Block;
  copy.id = newId;
  const blocks = [...slide.blocks];
  blocks.splice(idx + 1, 0, copy);
  return { ...slide, blocks };
}

/**
 * Move a block up (-1) or down (+1) among its siblings in the same anchor cell.
 * Only swaps with the nearest neighbour that shares the anchor — blocks at
 * other anchors stay untouched.
 */
export function reorderBlockWithinAnchor(
  slide: BlockSlide,
  blockId: string,
  dir: -1 | 1,
): BlockSlide {
  const idx = slide.blocks.findIndex((b) => b.id === blockId);
  if (idx < 0) return slide;
  const target = slide.blocks[idx];
  if (!target) return slide;

  const step = dir;
  let neighbourIdx = -1;
  for (let i = idx + step; i >= 0 && i < slide.blocks.length; i += step) {
    const candidate = slide.blocks[i];
    if (candidate && candidate.anchor === target.anchor) {
      neighbourIdx = i;
      break;
    }
  }
  if (neighbourIdx < 0) return slide;

  const blocks = [...slide.blocks];
  const tmp = blocks[idx]!;
  blocks[idx] = blocks[neighbourIdx]!;
  blocks[neighbourIdx] = tmp;
  return { ...slide, blocks };
}

// ── Flip / mirror operations ────────────────────────────────────────────

function flipBg(bg?: SlideBg): SlideBg | undefined {
  return flipBgFocalX(bg);
}

/**
 * Mirrors every block (and the slide chrome) horizontally so a layout
 * designed for, say, a bottom-right hero photo can be reused for one with
 * the subject on the left without rebuilding it. We flip:
 *   - block anchor (left ↔ right; centre stays)
 *   - block.x to its mirror around 50% so any drift inside a cell is preserved
 *   - block.textAlign (left ↔ right)
 *   - scrim direction (left ↔ right; vertical stays)
 *   - bg image position (left ↔ right)
 *
 * Image flipX is intentionally NOT toggled — that would mirror people /
 * embedded text inside the photo, which is rarely what the user wants.
 */
export function flipSlideHorizontally(slide: BlockSlide): BlockSlide {
  return {
    ...slide,
    bg: flipBg(slide.bg),
    scrim: slide.scrim
      ? { ...slide.scrim, direction: flipScrimDirectionHorizontally(slide.scrim.direction) }
      : slide.scrim,
    blocks: slide.blocks.map((b) => {
      const nextAnchor = flipAnchorHorizontally(b.anchor);
      // Snap x/y to the canonical anchor coordinates so the block sits in
      // the correct cell after the flip even if the source had drift.
      const p = ANCHOR_POSITIONS[nextAnchor];
      const next: Block = { ...b, anchor: nextAnchor, x: p.x, y: p.y };
      // Mirror text alignment for text blocks.
      if ('textAlign' in next && next.textAlign) {
        const ta = next.textAlign;
        (next as { textAlign?: 'left' | 'center' | 'right' }).textAlign =
          ta === 'left' ? 'right' : ta === 'right' ? 'left' : ta;
      }
      return next;
    }),
  };
}
